import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";

  /**
   * True while the confirmed request is in flight. Passing this prop at all switches the dialog
   * into async mode: confirming no longer closes it, so the caller owns the close and the wait is
   * shown here instead of behind a dialog that already vanished.
   *
   * Leaving it undefined keeps the original fire-and-forget behaviour, which the callers that
   * close synchronously still rely on.
   */
  isPending?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  isPending,
}: ConfirmDialogProps) {
  const isAsync = isPending !== undefined;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        onEscapeKeyDown={(event) => {
          // Cancel is disabled while the request runs, so Esc must not be a way around it —
          // this dialog is the only place the action reports back.
          if (isPending) {
            event.preventDefault();
          }
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogBody>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              // Radix closes on click; hold the dialog so the spinner covers the request.
              if (isAsync) {
                event.preventDefault();
              }
              onConfirm();
            }}
            disabled={isPending}
            className={variant === "destructive" ? "bg-destructive" : ""}
          >
            {isPending && <Spinner className="mr-2" />}
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
